import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { Gift, Sparkles, TrendingUp, Award, Lock, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Rewards() {
  const { user } = useAuth();
  const { t } = useLang();
  const [catalog, setCatalog] = useState([]);
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [address, setAddress] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const reload = async () => {
    try {
      const c = await api.get("/rewards/catalog");
      setCatalog(c.data.rewards || []);
      if (user) {
        const m = await api.get("/rewards/me");
        setMe(m.data);
      }
    } catch { /* silent */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user]);

  const onRedeem = async () => {
    setRedeeming(true);
    try {
      await api.post("/rewards/redeem", { reward_id: picked.id, shipping_address: address });
      toast.success(`Premio "${picked.name}" riscattato!`);
      setOpen(false); setAddress(""); setPicked(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore riscatto");
    } finally { setRedeeming(false); }
  };

  return (
    <div data-testid="rewards-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#7A1C2B] via-[#5A0F1F] to-[#3A050D] text-white py-16 md:py-24">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/rewards/rewards_card.png`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 animate-ken-burns" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C9A961 0%, transparent 50%), radial-gradient(circle at 80% 70%, #E2A03F 0%, transparent 50%)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3A050D]/80 via-[#5A0F1F]/60 to-transparent" />
        <div className="relative container-xl">
          <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] font-semibold mb-3">{t("section.rewards_title", "Programma Punti Premio")}</div>
          <h1 className="editorial-heading text-4xl md:text-6xl mb-4">
            Ogni euro speso, <span className="italic text-[#C9A961]">un punto guadagnato.</span>
          </h1>
          <p className="text-white/80 max-w-2xl text-base md:text-lg mb-8">
            {t("section.rewards_sub", "Riscatta box gourmet, esperienze in cantina e weekend in resort 5 stelle.")}
          </p>

          {user && me ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl" data-testid="rewards-stats">
              <Stat icon={Sparkles} label={t("common.points_balance", "Saldo punti")} value={me.balance} accent />
              <Stat icon={TrendingUp} label="Punti guadagnati" value={me.earned_points} />
              <Stat icon={Gift} label="Già riscattati" value={me.redeemed_points} />
              <Stat icon={ShoppingBag} label="Speso totale" value={`€ ${me.total_spent_eur.toLocaleString("it-IT")}`} />
            </div>
          ) : (
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-sm p-5 max-w-md" data-testid="rewards-login-cta">
              <div className="text-sm text-white/90 mb-3">{t("nav.login", "Accedi")} per visualizzare il tuo saldo punti.</div>
              <Link to="/login" className="inline-block px-5 py-2 bg-[#C9A961] text-black text-xs font-bold uppercase tracking-wider rounded-sm">{t("nav.login", "Accedi")}</Link>
            </div>
          )}
        </div>
      </section>

      {/* CATALOG */}
      <section className="container-xl py-12 md:py-16">
        <h2 className="editorial-heading text-3xl md:text-4xl mb-2">Il catalogo dei premi</h2>
        <p className="text-muted-foreground mb-8">Da 100 punti in su. Spedizione gratuita su tutti i premi.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rewards-grid">
          {catalog.map((r) => {
            const canRedeem = me?.balance >= r.points;
            return (
              <article key={r.id} className="bg-card border border-border rounded-sm overflow-hidden flex flex-col" data-testid={`reward-${r.id}`}>
                <div className="aspect-[4/3] bg-[hsl(var(--muted))] relative overflow-hidden">
                  <img src={r.image} alt={r.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-3 right-3 px-3 py-1 bg-[#C9A961] text-black text-xs font-bold rounded-full flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> {r.points} pt
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{r.category}</div>
                  <h3 className="editorial-heading text-xl mb-2">{r.name}</h3>
                  <p className="text-sm text-muted-foreground flex-1 mb-4">{r.description}</p>
                  <button
                    disabled={!user || !canRedeem}
                    onClick={() => { setPicked(r); setOpen(true); }}
                    data-testid={`redeem-btn-${r.id}`}
                    className={`w-full px-4 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 transition-colors ${
                      canRedeem ? "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" : "bg-[hsl(var(--muted))] text-muted-foreground cursor-not-allowed"
                    }`}
                  >
                    {!user ? <><Lock className="h-3.5 w-3.5" /> Accedi per riscattare</> : canRedeem ? <><Gift className="h-3.5 w-3.5" /> Riscatta</> : <><Lock className="h-3.5 w-3.5" /> {r.points - (me?.balance || 0)} pt mancanti</>}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[hsl(var(--muted))]/40 border-y border-border py-14">
        <div className="container-xl">
          <h2 className="editorial-heading text-3xl mb-8 text-center">Come funziona</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Step n={1} title="Acquista" desc="Vini, food box, esperienze, abbonamenti — su Amazonico 3000." />
            <Step n={2} title="Guadagna" desc="1 punto ogni €1 speso. I punti si accumulano automaticamente." />
            <Step n={3} title="Riscatta" desc="Dal catalogo: box, bottiglie, esperienze, weekend in resort." />
          </div>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Riscatta {picked?.name}</DialogTitle></DialogHeader>
          {picked && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Costo: <strong>{picked.points} punti</strong> · Saldo dopo: <strong>{(me?.balance || 0) - picked.points} punti</strong>
              </div>
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Indirizzo di spedizione</span>
                <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={3} className="w-full px-3 py-2 border border-border rounded-sm" placeholder="Via, città, CAP…" data-testid="redeem-address" />
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm">Annulla</button>
            <button onClick={onRedeem} disabled={redeeming || !address.trim()} data-testid="redeem-confirm" className="px-4 py-2 bg-[hsl(var(--primary))] text-white text-sm font-semibold rounded-sm disabled:opacity-50">
              {redeeming ? "Riscatto…" : "Conferma riscatto"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-4 border rounded-sm ${accent ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/20 bg-white/5"}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-white/70">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-[#C9A961]" : "text-white/60"}`} />
      </div>
      <div className={`text-2xl font-bold ${accent ? "text-[#C9A961]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center text-lg font-bold mb-3">{n}</div>
      <div className="editorial-heading text-xl mb-2">{title}</div>
      <div className="text-sm text-muted-foreground">{desc}</div>
    </div>
  );
}
